import { phoneCalls } from '@markku/db'
import { createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import {
  phoneCallCallbackReasonSchema,
  phoneCallOutcomeSchema,
} from './callingCampaign'

export const selectPhoneCallSchema = createSelectSchema(phoneCalls)

// What an officer is allowed to write via `phoneCall.update`. The call itself
// is owned by the webhook / WebRTC flow — officers only relink a call to a
// user/opportunity/customer (e.g. "Luo liidi tästä puhelusta"), mark the call
// as followed-up via `handledAt`, or edit their own summary / notes via
// `operatorNotes`. All other fields (recording/transcript/ai summary/routing
// stage/telnyx ids) are derived state and must not be writable from the
// client.
const nullableFk = z.number().nullable().optional()
export const upsertPhoneCallSchema = z
  .object({
    userId: nullableFk,
    opportunityId: nullableFk,
    customerId: nullableFk,
    handledAt: z.string().datetime().nullable().optional(),
    operatorNotes: z.string().nullable().optional(),
  })
  .strict()

export const selectPhoneCallWithRelationsSchema = selectPhoneCallSchema.extend({
  user: z
    .object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      phoneNumber: z.string().nullable(),
    })
    .nullable(),
  answerer: z
    .object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullable(),
  opportunity: z
    .object({
      id: z.number(),
      stage: z.string(),
    })
    .nullable(),
  customer: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .nullable(),
})

// Who a manually dialled number belongs to. `preCallSms` says what would
// actually happen on dial — 'internal' when the callee is staff,
// 'recently_sent' when one already went out inside the quiet window — so the
// dial UI never promises a message that gets skipped server-side.
export const numberMatchSchema = z.object({
  userId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  phoneNumber: z.string().nullable(),
  isStaff: z.boolean(),
  preCallSms: z.enum(['sends', 'internal', 'recently_sent']),
  opportunities: z.array(
    z.object({
      id: z.number(),
      stage: z.string(),
      contextName: z.string().nullable(),
    }),
  ),
  customers: z.array(z.object({ id: z.number(), name: z.string() })),
})

export const lookupNumberOutputSchema = z.object({
  normalized: z.string().nullable(),
  matches: z.array(numberMatchSchema),
})

// What the ring modal and the call panel show about the other party before
// anyone has clicked into a record: who last spoke to them and how it ended,
// whether a visit is already on the calendar, and whether the number is
// ambiguous. Built from the phone_calls row so it works for both directions.
export const callerBriefSchema = z.object({
  userId: z.number().nullable(),
  partyNumber: z.string(),
  // Everyone sharing the number, with their CRM context — more than one is a
  // warning, and the panel lets the operator pick which person the call is
  // really with.
  matches: z.array(numberMatchSchema),
  // Latest earlier call with this number, either direction.
  lastContact: z
    .object({
      id: z.number(),
      direction: z.enum(['inbound', 'outbound']),
      // Operator marked an outcome without a call taking place.
      dispositionOnly: z.boolean(),
      startedAt: z.string(),
      answered: z.boolean(),
      outcome: phoneCallOutcomeSchema.nullable(),
      callbackAt: z.string().nullable(),
      callbackReason: phoneCallCallbackReasonSchema.nullable(),
      aiSummary: z.string().nullable(),
      operatorNotes: z.string().nullable(),
      answerer: z
        .object({ firstName: z.string(), lastName: z.string() })
        .nullable(),
      campaign: z
        .object({
          id: z.number(),
          name: z.string(),
          queueMode: z.enum(['person', 'customer']),
        })
        .nullable(),
    })
    .nullable(),
  nextVisit: z
    .object({
      id: z.number(),
      startTime: z.string(),
      address: z.string().nullable(),
    })
    .nullable(),
})

export const recentCallNumberSchema = z.object({
  phoneNumber: z.string(),
  displayName: z.string().nullable(),
  direction: z.enum(['inbound', 'outbound']),
  startedAt: z.string(),
})

export type NumberMatch = z.infer<typeof numberMatchSchema>
export type CallerBrief = z.infer<typeof callerBriefSchema>
export type RecentCallNumber = z.infer<typeof recentCallNumberSchema>

export type PhoneCall = z.infer<typeof selectPhoneCallSchema>
export type PhoneCallWithRelations = z.infer<
  typeof selectPhoneCallWithRelationsSchema
>
export type UpsertPhoneCall = z.infer<typeof upsertPhoneCallSchema>

export type { PhoneCallActionItem, PhoneCallLeg } from '@markku/db'
